import { supabase } from '../supabase';
import type { Tables, TablesInsert, TablesUpdate } from '../database.types';
import {
  PaginationParams,
  PaginatedResponse,
  calculatePaginationRange,
  buildPaginationInfo,
} from './pagination';
import { logger } from '../logger';
import { withRetry } from './withRetry';

export type Reunion = Tables<'reuniones'>;
export type ReunionInsert = TablesInsert<'reuniones'>;
export type ReunionUpdate = TablesUpdate<'reuniones'>;
export type ReunionWithCounts = Reunion & {
  propuestasCount: number;
  gestionesCount: number;
};

export interface ReunionFilters {
  municipalityId?: string;
  status?: Reunion['status'];
  dateFrom?: string;
  dateTo?: string;
  searchText?: string;
}

/**
 * Helper function to add counts to reuniones
 * OPTIMIZED: Uses database RPC function for server-side aggregation
 * Before (SCALE-HIGH-003): Fetched ALL propuestas/gestiones rows to count client-side
 * After: Single RPC call with SQL COUNT() aggregation - no row transfer
 * Performance: O(n reuniones) vs O(total propuestas + gestiones rows)
 */
async function addCountsToReuniones(reuniones: Reunion[]): Promise<ReunionWithCounts[]> {
  if (reuniones.length === 0) return [];

  logger.log('[addCountsToReuniones] Processing', reuniones.length, 'reuniones (RPC aggregation)');

  // Extract reunion IDs and organization ID (all reuniones should have same org ID)
  const reunionIds = reuniones.map(r => r.id);
  const organizationId = reuniones[0]?.organization_id;

  if (!organizationId) {
    logger.error('[addCountsToReuniones] No organization_id found on reuniones');
    // Return with zero counts as fallback
    return reuniones.map(reunion => ({
      ...reunion,
      propuestasCount: 0,
      gestionesCount: 0,
    }));
  }

  // Call RPC function for efficient server-side counting (with retry)
  const { data: counts, error } = await withRetry(() =>
    supabase.rpc('get_reunion_counts', {
      p_reunion_ids: reunionIds,
      p_organization_id: organizationId,
    })
  );

  if (error) {
    logger.error('[addCountsToReuniones] Error calling RPC:', error);
    // Return with zero counts as fallback
    return reuniones.map(reunion => ({
      ...reunion,
      propuestasCount: 0,
      gestionesCount: 0,
    }));
  }

  // Build lookup map from RPC results
  const countMap = new Map<string, { propuestas: number; gestiones: number }>();
  (counts || []).forEach((c: { reunion_id: string; propuestas_count: number; gestiones_count: number }) => {
    countMap.set(c.reunion_id, {
      propuestas: Number(c.propuestas_count) || 0,
      gestiones: Number(c.gestiones_count) || 0,
    });
  });

  // Map reuniones with their counts
  return reuniones.map(reunion => ({
    ...reunion,
    propuestasCount: countMap.get(reunion.id)?.propuestas || 0,
    gestionesCount: countMap.get(reunion.id)?.gestiones || 0,
  }));
}

/**
 * Fetch reuniones with optional filters (with counts)
 * Uses RPC function when searchText is provided to enable searching by municipality name
 */
export async function getReuniones(filters?: ReunionFilters): Promise<ReunionWithCounts[]> {
  // Use RPC function when searchText is provided to search across municipality name
  if (filters?.searchText) {
    const { data, error } = await withRetry(() =>
      supabase.rpc('search_reuniones', {
        p_search_text: filters.searchText,
        p_municipality_id: filters.municipalityId ?? null,
        p_status: filters.status ?? null,
        p_date_from: filters.dateFrom ?? null,
        p_date_to: filters.dateTo ?? null,
      } as any)
    );

    if (error) throw error;

    const reuniones = (data || []) as Reunion[];
    return addCountsToReuniones(reuniones);
  }

  // Standard query when no search text (more efficient for simple filters)
  let query = supabase
    .from('reuniones')
    .select('*')
    .order('date', { ascending: false })
    .order('time', { ascending: false });

  if (filters?.municipalityId) {
    query = query.eq('municipality_id', filters.municipalityId);
  }

  if (filters?.status) {
    query = query.eq('status', filters.status);
  }

  if (filters?.dateFrom) {
    query = query.gte('date', filters.dateFrom);
  }

  if (filters?.dateTo) {
    query = query.lte('date', filters.dateTo);
  }

  const { data, error } = await withRetry(() => query);

  if (error) throw error;

  const reuniones = data || [];
  return addCountsToReuniones(reuniones);
}

/**
 * Fetch reuniones with pagination support
 * SCALE-CRIT-001 Fix: Prevents unbounded queries that can timeout at scale
 */
export async function getReunionesPaginated(
  filters?: ReunionFilters,
  pagination?: PaginationParams
): Promise<PaginatedResponse<ReunionWithCounts>> {
  const { from, to, page, pageSize } = calculatePaginationRange(pagination);

  // For RPC searches with pagination, we need to handle differently
  if (filters?.searchText) {
    // Get total count first (with retry)
    const { data: allData, error: countError } = await withRetry(() =>
      supabase.rpc('search_reuniones', {
        p_search_text: filters.searchText,
        p_municipality_id: filters.municipalityId ?? null,
        p_status: filters.status ?? null,
        p_date_from: filters.dateFrom ?? null,
        p_date_to: filters.dateTo ?? null,
      } as any)
    );

    if (countError) throw countError;

    const totalCount = (allData || []).length;
    // Manually paginate the RPC results (RPC doesn't support .range())
    const paginatedData = ((allData || []) as Reunion[]).slice(from, to + 1);
    const withCounts = await addCountsToReuniones(paginatedData);

    return {
      data: withCounts,
      pagination: buildPaginationInfo(totalCount, page, pageSize),
    };
  }

  // Get total count first for non-RPC queries
  let countQuery = supabase
    .from('reuniones')
    .select('*', { count: 'exact', head: true });

  if (filters?.municipalityId) {
    countQuery = countQuery.eq('municipality_id', filters.municipalityId);
  }
  if (filters?.status) {
    countQuery = countQuery.eq('status', filters.status);
  }
  if (filters?.dateFrom) {
    countQuery = countQuery.gte('date', filters.dateFrom);
  }
  if (filters?.dateTo) {
    countQuery = countQuery.lte('date', filters.dateTo);
  }

  const { count: totalCount, error: countError } = await withRetry(() => countQuery);
  if (countError) throw countError;

  // Get paginated data
  let query = supabase
    .from('reuniones')
    .select('*')
    .order('date', { ascending: false })
    .order('time', { ascending: false })
    .range(from, to);

  if (filters?.municipalityId) {
    query = query.eq('municipality_id', filters.municipalityId);
  }
  if (filters?.status) {
    query = query.eq('status', filters.status);
  }
  if (filters?.dateFrom) {
    query = query.gte('date', filters.dateFrom);
  }
  if (filters?.dateTo) {
    query = query.lte('date', filters.dateTo);
  }

  const { data, error } = await withRetry(() => query);
  if (error) throw error;

  const withCounts = await addCountsToReuniones(data || []);

  return {
    data: withCounts,
    pagination: buildPaginationInfo(totalCount || 0, page, pageSize),
  };
}

/**
 * Get reuniones for a specific date range (for calendar view) with counts
 */
export async function getReunionesForMonth(year: number, month: number): Promise<ReunionWithCounts[]> {
  const startDate = new Date(year, month, 1).toISOString().split('T')[0];
  const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];

  // getReuniones already returns with counts
  return getReuniones({
    dateFrom: startDate,
    dateTo: endDate,
  });
}

/**
 * Get a single reunion by ID
 */
export async function getReunionById(id: string): Promise<Reunion | null> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('reuniones')
      .select('*')
      .eq('id', id)
      .single()
  );

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data;
}

/**
 * Create a new reunion
 * NOTE: organization_id MUST be included in the reunion object
 */
export async function createReunion(reunion: ReunionInsert): Promise<Reunion> {
  logger.log('[createReunion] Creating reunion with data:', reunion);

  const { data, error } = await withRetry(() =>
    supabase
      .from('reuniones')
      .insert(reunion)
      .select()
      .single()
  );

  if (error) {
    logger.error('[createReunion] Error:', error);
    throw error;
  }

  logger.log('[createReunion] Success:', data);
  return data;
}

/**
 * Update an existing reunion
 */
export async function updateReunion(id: string, updates: ReunionUpdate): Promise<Reunion> {
  logger.log('[updateReunion] Updating reunion:', id, updates);

  const { data, error } = await withRetry(() =>
    supabase
      .from('reuniones')
      .update(updates)
      .eq('id', id)
      .select()
      .single()
  );

  if (error) {
    logger.error('[updateReunion] Error:', error);
    throw error;
  }

  logger.log('[updateReunion] Success:', data);
  return data;
}

/**
 * Delete a reunion
 */
export async function deleteReunion(id: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('reuniones')
      .delete()
      .eq('id', id)
  );

  if (error) throw error;
}

/**
 * Get count of propuestas linked to a reunion
 */
export async function getReunionPropuestasCount(reunionId: string): Promise<number> {
  const { count, error } = await supabase
    .from('propuestas')
    .select('*', { count: 'exact', head: true })
    .eq('reunion_id', reunionId);

  if (error) throw error;
  return count || 0;
}

/**
 * Get count of gestiones linked to a reunion
 */
export async function getReunionGestionesCount(reunionId: string): Promise<number> {
  const { count, error } = await supabase
    .from('gestiones')
    .select('*', { count: 'exact', head: true })
    .eq('reunion_id', reunionId);

  if (error) throw error;
  return count || 0;
}

/**
 * Get reunion title by ID (lightweight query for display purposes)
 * Used when we only need the title, not the full reunion data
 */
export async function getReunionTitleById(reunionId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('reuniones')
    .select('title')
    .eq('id', reunionId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    logger.error('[getReunionTitleById] Error:', error);
    return null;
  }
  return data?.title || null;
}

/**
 * Get multiple reunion titles by IDs (batch query for efficiency)
 * Returns a Map of reunionId -> title
 */
export async function getReunionTitlesByIds(reunionIds: string[]): Promise<Map<string, string>> {
  if (reunionIds.length === 0) return new Map();

  const uniqueIds = [...new Set(reunionIds)];

  const { data, error } = await supabase
    .from('reuniones')
    .select('id, title')
    .in('id', uniqueIds);

  if (error) {
    logger.error('[getReunionTitlesByIds] Error:', error);
    return new Map();
  }

  return new Map((data || []).map(r => [r.id, r.title]));
}

/**
 * Lightweight reunion option for dropdown selectors
 */
export interface ReunionOption {
  id: string;
  title: string;
  date: string;
  municipality_name: string | null;
}

/**
 * Get reuniones for dropdown selection (lightweight query)
 * Returns id, title, date, and municipality name - ordered by date descending
 */
export async function getReunionesForDropdown(): Promise<ReunionOption[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('reuniones')
      .select('id, title, date, municipalities(name)')
      .order('date', { ascending: false })
      .order('time', { ascending: false })
  );

  if (error) {
    logger.error('[getReunionesForDropdown] Error:', error);
    return [];
  }

  // Transform the joined data to flat structure
  return (data || []).map(r => ({
    id: r.id,
    title: r.title,
    date: r.date,
    municipality_name: (r.municipalities as { name: string } | null)?.name || null,
  }));
}
